import m from "mithril";
import i18n from "../../../shared/i18n/i18n";
import Auth from "../../../shared/auth";
import FormUtilities from "../../../shared/utilities/form_utilities";
import TextUtilities from "../../../shared/utilities/text_utilities";
import FormElementsMenu from "./FormElementsMenu";
import TextEditor from "../../../shared/components/component_text_editor";
import UndoHistory from "../shared/undo_history";
import { fieldHasOptions } from "../../../shared/utilities/assessment_dependencies";

let sectionCopy = null;
let originalSection = null;
let isNewSection = false;
let optionsForDeps = [];

// True while the section editor is on screen. Its edits live in sectionCopy and
// only reach the unit on "save section", so the page disables the checklist save
// button while the toolbox is open rather than writing out a unit without them.
let isMounted = false;

export function isOpen() {
    return isMounted;
}

// The toolbox lives on a mount root of its own inside the editor's tree, so
// tearing that tree down (switching tabs) drops the node without running
// onremove. The editor calls this as it remounts, where the elements menu is
// what gets rendered.
export function markClosed() {
    isMounted = false;
}

export function hasUnsavedChanges() {
    if (sectionCopy === null) {
        return false;
    }

    // Compare the whole copy rather than just name and description: the
    // dependencies select edits sectionCopy.prerequisiteOptionIds, and those
    // edits are thrown away on cancel exactly like the rest of the toolbox.
    // A section dragged in from the toolbox is not part of the saved checklist
    // until save_section commits it, even if it was never edited.
    return isNewSection || JSON.stringify(sectionCopy) !== originalSection;
}

// The toolbox keeps its state in module scope, so it outlives the mount. Callers
// that take the pending edits away must clear it, or hasUnsavedChanges keeps
// reporting edits that are already gone. Re-baselines rather than dropping
// sectionCopy, so a redraw of a toolbox that is still mounted keeps rendering
// what the user sees.
export function discardUnsavedChanges() {
    originalSection = sectionCopy === null ? null : JSON.stringify(sectionCopy);
    isNewSection = false;
}

function revertToolbox() {
    // Saved, deleted or cancelled: nothing is pending once the toolbox is gone.
    discardUnsavedChanges();
    m.mount(document.querySelector(".form-toolbox"), FormElementsMenu);
}

function saveUndoHistory() {
    UndoHistory.snapshot(undoHistory, unit);
}

function isNameValid() {
    return TextUtilities.hasVisibleText(TextUtilities.safeDecodeURIComponent(sectionCopy.name, true));
}

function saveSection() {
    if (FormUtilities.validateFields(".section-edit input") && isNameValid()) {
        saveUndoHistory();
        for (let i = 0; i < unit.sections.length; i++) {
            if (unit.sections[i].id === sectionCopy.id) {
                unit.sections.splice(i, 1, structuredClone(sectionCopy));
            }
        }
        isNewSection = false;
        revertToolbox();
        m.redraw();
    }
}

function removeSection() {
    for (let i = 0; i < unit.sections.length; i++) {
        if (unit.sections[i].id === sectionCopy.id) {
            saveUndoHistory();
            unit.sections.splice(i, 1);
        }
    }
    revertToolbox();
    m.redraw();
}

function checkSectionOps(opId) {
    for (let i = 0; i < sectionCopy.prerequisiteOptionIds.length; i++) {
        if (sectionCopy.prerequisiteOptionIds[i] === opId) {
            return true;
        }
    }
    return false;
}

function updateSectionOptionDependencies(options) {
    sectionCopy.prerequisiteOptionIds = [];
    for (let i = 0; i < options.length; i++) {
        if (options[i].selected) {
            sectionCopy.prerequisiteOptionIds.push(options[i].value);
        }
    }
}

function getSectionOptions(sectionId) {
    let options = [];
    for (let i = 0; i < unit.sections.length; i++) {
        if (unit.sections[i].id === sectionId) {
            return options;
        }

        for (let v = 0; v < unit.sections[i].fields.length; v++) {
            const field = unit.sections[i].fields[v];
            if (fieldHasOptions(field.type)) {
                for (let x = 0; x < field.options.length; x++) {
                    options.push(field.options[x]);
                }
            }
        }
    }

    return options;
}

let unit;
let undoHistory;

const EditSectionToolbox = {
    oninit(vnode) {
        unit = vnode.attrs.unit;
        undoHistory = vnode.attrs.undoHistory;
        sectionCopy = structuredClone(vnode.attrs.sec);
        originalSection = JSON.stringify(sectionCopy);
        isNewSection = !!vnode.attrs.isNew;
        optionsForDeps = getSectionOptions(sectionCopy.id);
        isMounted = true;
    },
    onremove() {
        isMounted = false;
    },
    view() {
        return [
            m(".form-toolbox", [
                m("h4.mb-2", i18n.t("edit_section")),
                sectionCopy ? [
                    m(".form.full-width.section-edit", [
                        m(".form-section.required", [
                            m(".form-label", i18n.t("name")),
                            m(TextEditor, {
                                content: encodeURIComponent(sectionCopy.name || ""),
                                simpleToolbar: true,
                                onchange(content) {
                                    sectionCopy.name = content;
                                }
                            }),
                            !isNameValid() ? m(".text-error.padding", { style: "font-size: 0.75rem;" }, i18n.t("complete_required_fields")) : ""
                        ]),
                        m(".form-section", [
                            m(".form-label", i18n.t("description")),
                            m(TextEditor, {
                                content: encodeURIComponent(sectionCopy.information || ""),
                                hideHyperlink: true,
                                onchange(content) {
                                    sectionCopy.information = content;
                                }
                            })
                        ]),
                        optionsForDeps.length > 0 ? [
                            m(".form-section", [
                                m(".form-label", i18n.t("dependencies")),
                                m("select[multiple].section-option-deps-select", {
                                    onchange() {
                                        updateSectionOptionDependencies(this.options);
                                    }
                                }, optionsForDeps.map(op =>
                                    m("option", {
                                        value: op.id,
                                        oncreate(e) {
                                            if (checkSectionOps(op.id)) {
                                                e.dom.setAttribute("selected", "selected");
                                            }
                                        }
                                    }, op.value)
                                ))
                            ])
                        ] : "",
                        m(".form-buttons", [
                            Auth.validateRights(["unit_form_edit"], [
                                m("button.btn-error.flex-float-left", { onclick: removeSection }, i18n.t("delete_section")),
                                m("button", { onclick: saveSection }, i18n.t("save_section"))
                            ]),
                            m("button.ml-1", { onclick: revertToolbox }, i18n.t("cancel"))
                        ]),
                        hasUnsavedChanges() ? m(".text-error.my-2", { style: "text-align: right;" }, [
                            m("i.icon-warning"), m("span.ml-2", i18n.t("unsaved_changes"))
                        ]) : ""
                    ])
                ] : m("button.ml-1", { onclick: revertToolbox }, i18n.t("cancel"))
            ])
        ];
    }
};

export default EditSectionToolbox;
